import { useState, useEffect, useRef, useCallback } from 'react';

/* ── palette ── */
const C = {
  bg:       '#0a0812',
  bg2:      '#0f0c1a',
  bg3:      '#141024',
  bg4:      '#1c1630',
  border:   'rgba(138,100,220,0.15)',
  border2:  'rgba(138,100,220,0.32)',
  text:     '#e8e0f5',
  text2:    '#9b8fc0',
  text3:    '#4e4570',
  purple:   '#a855f7',
  purpleDim:'#7c3aed',
  purpleGlow:'rgba(168,85,247,0.14)',
  teal:     '#2dd4bf',
  tealBg:   'rgba(45,212,191,0.10)',
  red:      '#f87171',
  redBg:    'rgba(248,113,113,0.10)',
  amber:    '#fbbf24',
  amberBg:  'rgba(251,191,36,0.09)',
};

/* ── data ── */
const TODAY = new Date('2026-03-18T00:00:00');
const END   = new Date('2026-05-08T00:00:00');
const START = new Date('2026-03-18T00:00:00');
const msDay = 86400000;
const daysLeft   = Math.ceil((END - TODAY) / msDay);
const totalSpan  = Math.ceil((END - START) / msDay);
const elapsed    = Math.ceil((TODAY - START) / msDay);
const pct        = Math.round(Math.max(0, Math.min(100, (elapsed / totalSpan) * 100)));

const HYPES = [
  "The stars aligned. Temporarily. Don't get used to it.",
  "Cthulhu noticed. He gave it a 6/10. That's basically a standing ovation.",
  "You have appeased the homework daemon. It will return.",
  "The SEP field shifted. This assignment is now Somebody Else's Problem.",
  "One fewer thing standing between you and the abyss. Nice.",
  "The elder gods are indifferent to your grade, but they respect the grind.",
  "You stared into the void. The void said 'turn it in by 11:59.'",
  "Ritual complete. Your sanity loss is offset by the grade.",
  "Technically this was always going to happen across all timelines. But still.",
  "The nameless ones have acknowledged your submission. Probably.",
  "R'lyeh rises, but your GPA rises faster.",
  "That assignment? Gone. Like it never existed. Like we all will.",
  "In the grand entropic heat death of the universe, this matters slightly.",
  "The SEP field successfully deflected that assignment onto your past self.",
  "Ph'nglui mglw'nafh — anyway, great job turning that in.",
  "That's one less thing whispering your name at 2am.",
  "The incomprehensible void gives you a thumbs up. It has too many thumbs.",
  "Done. Banished. Returned to the dimension from which it came.",
  "The ancient ones remain unimpressed, but so does everyone, so.",
  "Assignment obliterated. Temporarily. Like all things.",
];

const SUBJECT_COLORS = ['#a78bfa','#2dd4bf','#f472b6','#fb923c','#94a3b8','#60a5fa','#86efac'];

const SUBJECTS = [
  { name: 'Algebra II', color: SUBJECT_COLORS[0], assignments: [
    { name: 'U3GA3 L3-L4', date: '2026-03-06', type: 'graded' },
    { name: 'U3GA1 L1-L4', date: '2026-03-13', type: 'graded' },
    { name: 'Project over Unit 4 L1-2', date: '2026-03-27', type: 'graded' },
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Quiz over Unit 4 L1-2', date: '2026-04-10', type: 'graded' },
    { name: 'Quiz over Unit 4 L3-4', date: '2026-04-24', type: 'graded' },
    { name: 'Final Exam: Algebra 2A', date: '2026-05-18', type: 'final' },
  ]},
  { name: 'Biology', color: SUBJECT_COLORS[1], assignments: [
    { name: 'Other Evolutionary Mechanisms quiz', date: '2026-03-27', type: 'graded' },
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Cellular Energy quiz', date: '2026-04-10', type: 'graded' },
    { name: 'Disruptions in Energy Flow quiz', date: '2026-04-17', type: 'graded' },
    { name: 'Ecosystem Biodiversity quiz', date: '2026-05-01', type: 'graded' },
    { name: 'Carbon and Nitrogen Cycle quiz', date: '2026-05-01', type: 'graded' },
    { name: 'Investigate Community Interactions quiz', date: '2026-05-08', type: 'graded' },
    { name: 'Final Exam: Biology B', date: '2026-05-18', type: 'final' },
  ]},
  { name: 'English', color: SUBJECT_COLORS[2], assignments: [
    { name: 'Unit 3 Lessons 1.1–4.1', date: '2026-03-13', type: 'graded' },
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Unit 4 Lessons 1.1–4.1', date: '2026-04-10', type: 'graded' },
    { name: 'Unit 4 Lessons 1.2–4.2', date: '2026-04-24', type: 'graded' },
    { name: 'Memoir Summary and Synthesis', date: '2026-05-01', type: 'graded' },
    { name: 'Final Exam: English 1B', date: '2026-05-18', type: 'final' },
  ]},
  { name: 'Fitness', color: SUBJECT_COLORS[3], assignments: [
    { name: 'Graded assignment', date: '2026-03-27', type: 'graded' },
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Graded quiz (Apr 10)', date: '2026-04-10', type: 'graded' },
    { name: 'Graded quiz (Apr 17)', date: '2026-04-17', type: 'graded' },
    { name: 'Final Exam: LFW B', date: '2026-05-18', type: 'final' },
  ]},
  { name: 'Finances', color: SUBJECT_COLORS[4], assignments: [
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Graded quiz (Apr 10)', date: '2026-04-10', type: 'graded' },
    { name: 'Graded quiz (Apr 24)', date: '2026-04-24', type: 'graded' },
    { name: 'Final Exam: Personal Financial Literacy', date: '2026-05-18', type: 'final' },
  ]},
  { name: 'Spanish I', color: SUBJECT_COLORS[5], assignments: [
    { name: 'Somos Estudiantes quiz', date: '2026-02-20', type: 'graded' },
    { name: 'Estar & Ir quiz', date: '2026-02-27', type: 'graded' },
    { name: 'México, Arte, Educación y Más quiz', date: '2026-02-27', type: 'graded' },
    { name: 'Conociendo Puerto Rico quiz', date: '2026-03-06', type: 'graded' },
    { name: 'La Comida quiz', date: '2026-03-13', type: 'graded' },
    { name: 'Nuestra Familia quiz', date: '2026-03-27', type: 'graded' },
    { name: 'Comparaciones quiz', date: '2026-04-03', type: 'graded' },
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Verbos con Cambio de Raíz quiz', date: '2026-04-10', type: 'graded' },
    { name: 'Verbos con cambio de raíz (o-ue, e-i) quiz', date: '2026-04-24', type: 'graded' },
    { name: 'La vida social quiz', date: '2026-05-08', type: 'graded' },
    { name: 'Final Exam: Spanish 1A', date: '2026-05-18', type: 'final' },
  ]},
  { name: 'World Geography', color: SUBJECT_COLORS[6], assignments: [
    { name: 'Unit 7 Quiz', date: '2026-03-13', type: 'graded' },
    { name: 'Discussion Board: Unit 7', date: '2026-03-27', type: 'graded' },
    { name: '2nd Six Weeks Exam', date: '2026-04-03', type: 'exam' },
    { name: 'Industry SAQ', date: '2026-04-10', type: 'graded' },
    { name: 'Industry Quiz', date: '2026-04-17', type: 'graded' },
    { name: 'Unit 8 Quiz', date: '2026-04-24', type: 'graded' },
    { name: 'Culture SAQ', date: '2026-05-01', type: 'graded' },
    { name: 'Unit 9 Quiz', date: '2026-05-08', type: 'graded' },
    { name: 'Discussion Board: Units 8 & 9', date: '2026-05-08', type: 'graded' },
    { name: 'Final Exam: World Geography B', date: '2026-05-18', type: 'final' },
  ]},
];

const STORAGE_KEY = 'eldritch_tracker_v2';
const CONF_COLORS = ['#a78bfa','#7c3aed','#2dd4bf','#f472b6','#fb923c','#f87171','#fbbf24','#60a5fa'];

/* ── helpers ── */
function isPast(d)    { return new Date(d + 'T23:59:59') < TODAY; }
function daysUntil(d) { return Math.ceil((new Date(d + 'T00:00:00') - TODAY) / msDay); }
function fmtDate(d)   { return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
function rand(arr)    { return arr[Math.floor(Math.random() * arr.length)]; }

/* ── confetti canvas ── */
function ConfettiCanvas({ trigger, ox, oy }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!trigger) return;
    const canvas = ref.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    canvas.width = parent.offsetWidth;
    canvas.height = parent.offsetHeight;
    const ctx = canvas.getContext('2d');
    let pts = Array.from({ length: 65 }, () => ({
      x: ox, y: oy,
      vx: (Math.random() - 0.5) * 11,
      vy: -(Math.random() * 9 + 3),
      size: Math.random() * 7 + 3,
      color: rand(CONF_COLORS),
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.25,
      shape: Math.random() > 0.4 ? 'rect' : 'circle',
      life: 1,
    }));
    let raf;
    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pts = pts.filter(p => p.life > 0.04);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.3; p.vx *= 0.99;
        p.rot += p.rotV; p.life -= 0.016;
        ctx.save();
        ctx.globalAlpha = Math.min(1, p.life * 2);
        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 'rect') ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        else { ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      });
      if (pts.length) raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, [trigger, ox, oy]);

  return (
    <canvas ref={ref} style={{
      position: 'absolute', top: 0, left: 0,
      width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: 98,
    }} />
  );
}

/* ── pill ── */
function Pill({ children, bg, color, border }) {
  return (
    <span style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 20,
      background: bg, color, border: `0.5px solid ${border || 'transparent'}`,
      whiteSpace: 'nowrap', fontFamily: "'IM Fell English', Georgia, serif",
      letterSpacing: '0.02em',
    }}>{children}</span>
  );
}

/* ── assignment row ── */
function AssignmentRow({ a, isDone, onToggle }) {
  const ref = useRef(null);
  const past = isPast(a.date);
  const du = daysUntil(a.date);
  const borderColor = isDone ? C.text3 : past ? C.red : C.purpleDim;

  let cdPill = null;
  if (!isDone && a.type !== 'final') {
    if (past)     cdPill = <Pill bg={C.redBg}   color={C.red}   border="rgba(248,113,113,0.25)">devoured</Pill>;
    else if (du === 0) cdPill = <Pill bg={C.redBg}   color={C.red}   border="rgba(248,113,113,0.25)">it is here</Pill>;
    else if (du <= 7)  cdPill = <Pill bg={C.amberBg} color={C.amber} border="rgba(251,191,36,0.25)">{du}d until it wakes</Pill>;
    else               cdPill = <Pill bg={C.purpleGlow} color="#c084fc" border="rgba(124,58,237,0.3)">{du}d in the void</Pill>;
  }

  const typeProps = a.type === 'final'
    ? { bg: 'rgba(248,113,113,0.08)', color: '#fca5a5', border: 'rgba(248,113,113,0.2)', label: 'Final' }
    : a.type === 'exam'
    ? { bg: 'rgba(168,85,247,0.10)', color: '#c084fc', border: 'rgba(168,85,247,0.25)', label: 'Exam' }
    : { bg: C.tealBg, color: C.teal, border: 'rgba(45,212,191,0.2)', label: 'Graded' };

  return (
    <div ref={ref} style={{
      display: 'flex', alignItems: 'center', padding: '9px 12px',
      background: C.bg2, border: `0.5px solid ${C.border}`,
      borderLeft: `2px solid ${borderColor}`,
      gap: 10, opacity: isDone ? 0.35 : 1,
      transition: 'opacity 0.3s',
    }}>
      {/* checkbox */}
      <div
        onClick={e => onToggle(a, e.currentTarget)}
        style={{
          width: 17, height: 17, borderRadius: 3, flexShrink: 0,
          border: isDone ? 'none' : `1px solid ${C.border2}`,
          background: isDone ? C.purpleDim : 'transparent',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.2s',
        }}
      >
        {isDone && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 3.5L3.8 7L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>

      {/* name */}
      <span style={{
        fontSize: 13, flex: 1, minWidth: 0,
        fontFamily: "'IM Fell English', Georgia, serif",
        color: isDone ? C.text3 : C.text,
        textDecoration: isDone ? 'line-through' : 'none',
      }}>{a.name}</span>

      {/* badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        {cdPill}
        <Pill bg={C.bg3} color={C.text3} border={C.border}>{fmtDate(a.date)}</Pill>
        <Pill bg={typeProps.bg} color={typeProps.color} border={typeProps.border}>{typeProps.label}</Pill>
      </div>
    </div>
  );
}

/* ── main app ── */
export default function App() {
  const [checked, setChecked] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); }
    catch { return new Set(); }
  });
  const [filter, setFilter] = useState('All');
  const [toast, setToast] = useState('');
  const [toastOn, setToastOn] = useState(false);
  const [confetti, setConfetti] = useState(null);
  const wrapRef = useRef(null);
  const toastTimer = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...checked])); }
    catch {}
  }, [checked]);

  const toggle = useCallback((a, el) => {
    const key = a.name + a.date;
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); return next; }
      next.add(key);
      const r = el.getBoundingClientRect();
      const w = wrapRef.current.getBoundingClientRect();
      setConfetti({ id: Date.now(), ox: r.left - w.left + r.width / 2, oy: r.top - w.top + r.height / 2 });
      setToast(rand(HYPES));
      setToastOn(true);
      clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToastOn(false), 3400);
      return next;
    });
  }, []);

  const nonFinals = SUBJECTS.flatMap(s => s.assignments.filter(a => a.type !== 'final'));
  const totalCount   = nonFinals.length;
  const pastDueCount = nonFinals.filter(a => isPast(a.date)).length;
  const checkedCount = checked.size;

  const FILTERS = ['All', 'Upcoming', 'Past due', 'Completed', 'Exams'];

  return (
    <div ref={wrapRef} style={{
      position: 'relative', minHeight: '100vh',
      background: C.bg, color: C.text,
      fontFamily: "'IM Fell English', Georgia, serif",
      padding: '1.5rem 1rem 3rem',
      maxWidth: 740, margin: '0 auto',
    }}>
      {confetti && <ConfettiCanvas key={confetti.id} trigger={confetti.id} ox={confetti.ox} oy={confetti.oy} />}

      {/* toast */}
      <div style={{
        position: 'absolute', top: 14, left: '50%',
        transform: toastOn ? 'translateX(-50%) scale(1)' : 'translateX(-50%) scale(0.88)',
        background: C.bg3, border: `0.5px solid ${C.border2}`,
        borderRadius: 10, padding: '10px 20px',
        fontSize: 12, fontStyle: 'italic', color: C.text,
        maxWidth: '86%', textAlign: 'center',
        opacity: toastOn ? 1 : 0,
        transition: 'opacity 0.25s, transform 0.25s',
        pointerEvents: 'none', zIndex: 99,
      }}>{toast}</div>

      {/* title */}
      <div style={{ textAlign: 'center', marginBottom: '1.75rem', paddingTop: '0.5rem' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.18em', color: C.text3, textTransform: 'uppercase', marginBottom: 6 }}>
          Spring 2026
        </div>
        <h1 style={{
          fontSize: 28, fontWeight: 400, margin: 0,
          color: C.text, letterSpacing: '0.02em',
        }}>The Eldritch Assignment Tracker</h1>
        <div style={{ fontSize: 11, color: C.text3, fontStyle: 'italic', marginTop: 6 }}>
          what the void demands of you
        </div>
        <div style={{ width: 60, height: 1, background: `linear-gradient(90deg, transparent, ${C.purpleDim}, transparent)`, margin: '14px auto 0' }} />
      </div>

      {/* banner */}
      <div style={{
        background: C.bg2, border: `0.5px solid ${C.border2}`,
        borderRadius: 12, padding: '1.25rem 1.5rem', marginBottom: '1.5rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 10, color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>The void is consuming</div>
            <div style={{ fontSize: 26, fontWeight: 400, color: C.text }}>{daysLeft} days remain</div>
            <div style={{ fontSize: 11, color: C.text3, fontStyle: 'italic', marginTop: 2 }}>Until May 8 — last ritual before finals</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: C.text3, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 3 }}>Semester consumed</div>
            <div style={{ fontSize: 22, fontWeight: 400, color: C.text }}>{pct}%</div>
            <div style={{ fontSize: 11, color: C.text3, fontStyle: 'italic', marginTop: 2 }}>Mar 18 → May 8 · time is not real</div>
          </div>
        </div>
        <div style={{ height: 5, background: C.bg4, borderRadius: 5, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: `linear-gradient(90deg, #7c3aed, #a855f7, #2dd4bf)`,
            borderRadius: 5, transition: 'width 0.6s',
          }} />
        </div>
      </div>

      {/* stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10, marginBottom: '1.5rem' }}>
        {[
          { val: totalCount,   lbl: 'Offerings demanded', color: '#c084fc' },
          { val: pastDueCount, lbl: 'Consumed by time',   color: C.red },
          { val: checkedCount, lbl: 'Rituals completed',  color: C.teal },
        ].map(s => (
          <div key={s.lbl} style={{ background: C.bg2, border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 400, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, color: C.text3, marginTop: 3, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '1rem' }}>
        {FILTERS.map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            fontSize: 11, padding: '5px 13px', borderRadius: 20,
            border: filter === f ? `0.5px solid ${C.purple}` : `0.5px solid ${C.border}`,
            background: filter === f ? C.purpleGlow : 'transparent',
            color: filter === f ? C.purple : C.text2,
            fontWeight: filter === f ? 500 : 400,
            cursor: 'pointer', letterSpacing: '0.04em',
            fontFamily: "'IM Fell English', Georgia, serif",
            transition: 'all 0.15s',
          }}>{f}</button>
        ))}
      </div>

      {/* legend */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'center' }}>
        {[[C.red,'past due'],[C.purpleDim,'upcoming'],[C.text3,'completed']].map(([c,l]) => (
          <div key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.text3 }}>
            <div style={{ width:18, height:2, borderRadius:2, background:c }} />{l}
          </div>
        ))}
        <Pill bg={C.tealBg} color={C.teal} border="rgba(45,212,191,0.2)">Graded</Pill>
        <Pill bg="rgba(168,85,247,0.10)" color="#c084fc" border="rgba(168,85,247,0.25)">Exam</Pill>
        <Pill bg="rgba(248,113,113,0.08)" color="#fca5a5" border="rgba(248,113,113,0.2)">Final</Pill>
      </div>

      {/* subject sections */}
      {SUBJECTS.map(sub => {
        let list = sub.assignments;
        if (filter === 'Upcoming')  list = list.filter(a => !isPast(a.date) && !checked.has(a.name + a.date));
        else if (filter === 'Past due')  list = list.filter(a => isPast(a.date) && !checked.has(a.name + a.date));
        else if (filter === 'Exams')     list = list.filter(a => a.type === 'exam' || a.type === 'final');
        else if (filter === 'Completed') list = list.filter(a => checked.has(a.name + a.date));
        if (!list.length) return null;

        return (
          <div key={sub.name} style={{ marginBottom: '1.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: sub.color, flexShrink: 0, boxShadow: `0 0 6px ${sub.color}88` }} />
              <span style={{ fontSize: 12, color: C.text2, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{sub.name}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {list.map(a => (
                <AssignmentRow
                  key={a.name + a.date}
                  a={a}
                  isDone={checked.has(a.name + a.date)}
                  onToggle={toggle}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* footer */}
      <div style={{
        marginTop: '2.5rem', paddingTop: '1rem',
        borderTop: `0.5px solid ${C.border}`,
        fontSize: 11, color: C.text3, textAlign: 'center', fontStyle: 'italic', letterSpacing: '0.04em',
      }}>
        Progress is saved in this browser. The void remembers.
      </div>
    </div>
  );
}
